import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDialogStyles } from "../../theme/redesign";
import { formatDateTime } from "./helpers";
import { tNegocio } from "./shared";

const t = (key) => tNegocio(`pages.publicidad.plans.pay.${key}`);
const POLL_MS = 10000;

const useStyles = makeStyles((theme) => ({
  methods: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, marginBottom: 14 },
  method: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 14,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  methodOn: { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 1px ${theme.palette.primary.main}` },
  methodSub: { fontWeight: 400, fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  box: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: 14,
    fontSize: 13,
    lineHeight: 1.5,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
  },
  amount: { fontSize: 26, fontWeight: 800, color: "#047857", fontVariantNumeric: "tabular-nums" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  label: { fontSize: 12, color: theme.palette.text.secondary },
  value: { fontWeight: 700, wordBreak: "break-all" },
  steps: { margin: "8px 0 0", paddingLeft: 18 },
  done: { display: "flex", alignItems: "center", gap: 8, color: "#047857", fontWeight: 700 },
  waiting: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: theme.palette.text.secondary },
}));

const copy = (text) => {
  navigator.clipboard?.writeText(String(text || "")).then(() => toast.success(t("copied"))).catch(() => undefined);
};

function Line({ label, value, copyable }) {
  const classes = useStyles();
  return (
    <div className={classes.row} style={{ marginTop: 6 }}>
      <div>
        <div className={classes.label}>{label}</div>
        <div className={classes.value}>{value || "—"}</div>
      </div>
      {copyable && value ? (
        <Tooltip title={t("copy")}>
          <IconButton size="small" onClick={() => copy(value)}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
        </Tooltip>
      ) : null}
    </div>
  );
}

/** Pago de una orden de Publicidad IA: Binance Pay (se valida solo) o comprobante para el administrador. */
export default function PublicidadPayDialog({ order, onClose, onChanged }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState(null);
  const [payment, setPayment] = useState(null);
  const [txId, setTxId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const open = !!order;

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setChoice(null);
    setPayment(null);
    setTxId("");
    setFile(null);
    api.get("/negocio/publicidad/partner/payment-options")
      .then(({ data }) => { if (active) setOptions(data); })
      .catch((error) => { if (active) toastError(error); });
    if (order?.paymentStatus === "binance_pending") {
      setChoice("binance");
      setPayment({ ...order, amountUsdt: Number(order.amountUsdt) });
    }
    return () => { active = false; };
  }, [open, order?.orderId]);

  // Mientras espera Binance, consulta la orden: el sondeo del servidor la marca pagada sola.
  useEffect(() => {
    if (!open || payment?.paymentStatus !== "binance_pending") return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/negocio/publicidad/partner/orders", { params: { limit: 40 } });
        const row = (data?.records || []).find((r) => r.orderId === order.orderId);
        if (row?.paymentStatus === "paid") {
          setPayment((p) => ({ ...p, paymentStatus: "paid", partnerError: row.partnerError }));
          onChanged?.();
        }
      } catch {
        /* reintenta en el próximo ciclo */
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [open, payment?.paymentStatus, order?.orderId]);

  const run = async (work) => {
    setBusy(true);
    try { await work(); } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const pickBinance = () => run(async () => {
    setChoice("binance");
    const { data } = await api.post(`/negocio/publicidad/partner/orders/${encodeURIComponent(order.orderId)}/binance`);
    setPayment(data);
    onChanged?.();
  });

  const verifyBinance = () => run(async () => {
    const { data } = await api.post(`/negocio/publicidad/partner/orders/${encodeURIComponent(order.orderId)}/binance-confirm`, { txId: txId.trim() });
    setPayment(data);
    if (data.paymentStatus === "paid") toast.success(t("paidOk"));
    onChanged?.();
  });

  const sendReceipt = () => run(async () => {
    const body = new FormData();
    body.append("paymentMethodId", String(choice.id));
    body.append("file", file);
    await api.post(`/negocio/publicidad/partner/orders/${encodeURIComponent(order.orderId)}/receipt`, body);
    toast.success(t("receiptOk"));
    onChanged?.();
    onClose();
  });

  const usd = Number(order?.monto || 0);
  const local = options?.exchangeRate && options?.localCurrency
    ? Math.round(usd * options.exchangeRate).toLocaleString()
    : null;
  const paid = payment?.paymentStatus === "paid";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ className: dlg.paper }}>
      <DialogTitle>{t("title").replace("{plan}", order?.planId || "").replace("{monto}", `${order?.moneda || "USD"} ${order?.monto || ""}`)}</DialogTitle>
      <DialogContent>
        {!options ? <CircularProgress size={22} /> : (
          <>
            <div className={classes.label} style={{ marginBottom: 8 }}>{t("choose")}</div>
            <div className={classes.methods}>
              {options.binance?.enabled ? (
                <div
                  className={`${classes.method}${choice === "binance" ? ` ${classes.methodOn}` : ""}`}
                  onClick={busy || paid ? undefined : pickBinance}
                  role="button"
                  tabIndex={0}
                >
                  Binance Pay
                  <div className={classes.methodSub}>{t("binanceAuto")}</div>
                </div>
              ) : null}
              {options.methods.map((m) => (
                <div
                  key={m.id}
                  className={`${classes.method}${choice?.id === m.id ? ` ${classes.methodOn}` : ""}`}
                  onClick={busy || paid ? undefined : () => setChoice(m)}
                  role="button"
                  tabIndex={0}
                >
                  {m.name}
                  <div className={classes.methodSub}>{t("manualReview")}</div>
                </div>
              ))}
            </div>
            {!options.binance?.enabled && !options.methods.length ? <div className={classes.box}>{t("noMethods")}</div> : null}

            {choice === "binance" && payment ? (
              <div className={classes.box}>
                {paid ? (
                  <div className={classes.done}>
                    <CheckCircleIcon /> {payment.partnerError ? t("paidActivating") : t("paidOk")}
                  </div>
                ) : (
                  <>
                    <div className={classes.label}>{t("sendExactly")}</div>
                    <div className={classes.row}>
                      <span className={classes.amount}>{Number(payment.amountUsdt).toFixed(2)} USDT</span>
                      <Tooltip title={t("copy")}>
                        <IconButton size="small" onClick={() => copy(Number(payment.amountUsdt).toFixed(2))}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </div>
                    <Line label="Pay ID" value={payment.payId} copyable />
                    <Line label={t("validUntil")} value={payment.binanceExpiresAt ? formatDateTime(payment.binanceExpiresAt) : "—"} />
                    <ol className={classes.steps}>
                      <li>{t("binanceStep1")}</li>
                      <li>{t("binanceStep2")}</li>
                      <li>{t("binanceStep3")}</li>
                    </ol>
                    <div className={classes.waiting}><CircularProgress size={14} /> {t("waiting")}</div>
                    <TextField
                      id="publicidad-binance-txid"
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      size="small"
                      label={t("txId")}
                      value={txId}
                      onChange={(e) => setTxId(e.target.value)}
                    />
                  </>
                )}
              </div>
            ) : null}

            {choice && choice !== "binance" ? (
              <div className={classes.box}>
                <div className={classes.label}>{t("transfer")}</div>
                <div className={classes.amount}>USD {order?.monto}</div>
                {local ? <div className={classes.label}>≈ {options.localCurrency} {local}</div> : null}
                <Line label={choice.type || t("account")} value={choice.accountNumber} copyable />
                <Line label={t("holder")} value={choice.reference} />
                {choice.notes ? <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{choice.notes}</div> : null}
                <div style={{ marginTop: 12 }}>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: "none" }}
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  <Button variant="outlined" size="small" onClick={() => fileInput.current?.click()}>
                    {file ? file.name : t("pickReceipt")}
                  </Button>
                  <div className={classes.label} style={{ marginTop: 6 }}>{t("receiptHint")}</div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
        {choice === "binance" && payment && !paid ? (
          <Button color="primary" variant="contained" disabled={busy || !txId.trim()} onClick={verifyBinance}>{t("verify")}</Button>
        ) : null}
        {choice && choice !== "binance" ? (
          <Button color="primary" variant="contained" disabled={busy || !file} onClick={sendReceipt}>{t("sendReceipt")}</Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
